import { AssetResponse, isSportsEvent } from "../FetchAssetDetails/types";
import { providerLogoTheme, TileFormat } from "../GetTileFormat/types";

import { StreamingIcons } from "@/assets/iconFont/streaming-icons";

/**
 * Retrieves the provider logo URL from the response object.
 *
 * @param asset - The AssetResponse object containing details about the asset.
 * @param logoTheme - The desired logo theme, either "Dark", "Light", or "Default". Defaults to "Default".
 * @returns The formatted provider logo URL or `null` if no default logo is found.
 *
 */
const getProviderLogo = (
  asset: AssetResponse,
  logoTheme: providerLogoTheme = providerLogoTheme.Default,
): string | null => {
  /** Check if channel exists on this asset type */
  if (!("channel" in asset.attributes)) {
    return null;
  }

  const providerLogo = asset.attributes.channel?.logo?.find(
    (l: { type: string; key: string; template: string }) =>
      l.type === logoTheme,
  );
  if (providerLogo) {
    return providerLogo.template
      .replace("{key}", providerLogo.key)
      .replace("{width}", "0")
      .replace("{height}", "0");
  }
  return null;
};

/**
 * Formats a given `Date` object into a time string.
 *
 * @param date - The `Date` object to format.
 * @returns A formatted time string in the format "HH:MM AM/PM".
 *
 */
const formatTime = (date: Date): string =>
  date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

/**
 * Formats a given `Date` object into a date string.
 *
 * @param date - The `Date` object to format.
 * @returns A formatted date string in the format "MM/DD".
 *
 */
const formatDate = (date: Date): string =>
  `${date.getMonth() + 1}/${date.getDate()}`;

/**
 * Generates a badge string for a sports event based on its start time.
 *
 * @param asset - The AssetResponse object containing details about the asset.
 * @returns A formatted badge string for the sports event or `null` if the asset is not a sports event.
 *
 * @example
 * ```typescript
 * const asset: AssetResponse = {
 *   attributes: {
 *     displayStartTime: "2023-10-15T13:45:00",
 *   },
 * };
 * const badgeString = fetchBadgeString(response);
 * console.log(badgeString); // Outputs: "1:45 PM" if the event is today or "10/15" otherwise.
 * ```
 */
const fetchBadgeString = (asset: AssetResponse): string | null => {
  if (isSportsEvent(asset)) {
    const badgeDate = new Date(asset.attributes.displayStartTime);
    const today = new Date();
    if (
      today.getDay() == badgeDate.getDay() &&
      today.getMonth() == badgeDate.getMonth() &&
      today.getFullYear() == badgeDate.getFullYear()
    ) {
      return formatTime(badgeDate);
    } else {
      return formatDate(badgeDate);
    }
  } else return null;
};

/**
 * Generates a formatted Title Basics date for a sports event based on its start time.
 * Returns [Month Day, HH:MM AM/PM] for upcoming events
 * Returns [Weekday, Month Day] for replay events
 *
 * @param response - The AssetResponse object containing details about the asset.
 * @returns A formatted date string for the sports event or `null` if the asset is not a sports event.
 *
 */
const formatTitleBasicsDate = (response: AssetResponse): string[] | null => {
  if (isSportsEvent(response)) {
    const date = new Date(response.attributes.displayStartTime);
    const today = new Date();
    const monthDay = new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
    }).format(date);

    /** Upcoming events */
    if (date.getTime() > today.getTime()) {
      const time = formatTime(date);

      /*Check if event is today */
      const isToday =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

      if (isToday) {
        return ["Today ", `${time}`];
      } else {
        return [`${monthDay}`, ", ", `${time}`];
      }
    } else {
      /** Replay events */
      const weekday = new Intl.DateTimeFormat("en-US", {
        weekday: "long",
      }).format(date);
      return [`${weekday}, ", ", ${monthDay}`];
    }
  } else {
    return null;
  }
};

/**
 * Retrieves genre strings from an asset response with flexible options.
 *
 * @param asset - The AssetResponse object containing genre information.
 * @param options - Configuration options:
 *   - maxGenres: Maximum number of genres to return (default: 2)
 *   - separator: String to use for separating genres (default: " • ")
 *   - indices: Specific genre indices to retrieve (e.g., [0, 2] for 1st and 3rd genre)
 * @returns A formatted string of genres or `null` if no genres are found.
 *
 * @example
 * ```typescript
 * const asset: AssetResponse = {
 *   attributes: {
 *     genres: ["Kids", "Comedy", "Musical", "Animation", "Adventure"],
 *   },
 * };
 *
 * // Get first 2 genres
 * const genreString = fetchGenreString(asset);
 * console.log(genreString); // Outputs: "Kids • Comedy"
 *
 * // Get first genre only
 * const genreString2 = fetchGenreString(asset, { maxGenres: 1 });
 * console.log(genreString2); // Outputs: "Kids"
 *
 * // Get specific genres by index (1st and 3rd)
 * const genreString3 = fetchGenreString(asset, { indices: [0, 2] });
 * console.log(genreString3); // Outputs: "Kids • Musical"
 *
 * // Get 3rd genre only
 * const genreString4 = fetchGenreString(asset, { indices: [2] });
 * console.log(genreString4); // Outputs: "Musical"
 * ```
 */
const fetchGenreString = (
  asset: AssetResponse,
  options?: {
    maxGenres?: number;
    separator?: string;
    indices?: number[];
  },
): string | null => {
  const { maxGenres = 2, separator = " • ", indices } = options || {};

  /** Check if genres exists on this asset type */
  if (!("genres" in asset.attributes)) {
    return null;
  }

  const genres = (asset.attributes as any).genres as string[] | undefined;

  if (!genres || genres.length === 0) {
    return null;
  }

  let selectedGenres: string[];

  if (indices && indices.length > 0) {
    /** Get specific genres by index */
    selectedGenres = indices
      .map((index) => genres[index])
      .filter((genre) => genre !== undefined);
  } else {
    /** Get first N genres */
    selectedGenres = genres.slice(0, maxGenres);
  }

  return selectedGenres.length > 0 ? selectedGenres.join(separator) : null;
};

//Showcase Carousel Tile Utils

/**
 * Determines the appropriate button content (icon and text) for showcase carousel tiles based on tile format.
 *
 * @param tileFormat - The TileFormat enum value indicating the type of content being displayed.
 * @returns An object containing the icon (or null) and text string for the button.
 *
 * @example
 * ```typescript
 * // For a VOD asset
 * const buttonContent = getShowcaseButtonContent(TileFormat.VOD);
 * console.log(buttonContent); // Outputs: { icon: StreamingIcons.Play, text: "Watch Now" }
 *
 * // For an upcoming sports event
 * const buttonContent2 = getShowcaseButtonContent(TileFormat.Upcoming);
 * console.log(buttonContent2); // Outputs: { icon: null, text: "More Info" }
 *
 * // For a replay event
 * const buttonContent3 = getShowcaseButtonContent(TileFormat.Replay);
 * console.log(buttonContent3); // Outputs: { icon: StreamingIcons.Play, text: "Watch Replay" }
 * ```
 */
const getShowcaseButtonContent = (
  tileFormat: TileFormat,
): { icon: StreamingIcons | null; text: string } => {
  //Specific formats that do not default to "Watch Now" and a play icon
  if (tileFormat === TileFormat.Upcoming)
    return { icon: null, text: "More Info" };
  if (tileFormat === TileFormat.Replay)
    return { icon: StreamingIcons.Play, text: "Watch Replay" };
  if (tileFormat === TileFormat.Collection)
    return { icon: null, text: "Explore Collection" };
  if (tileFormat === TileFormat.Hub) return { icon: null, text: "Explore Hub" };
  if (tileFormat === TileFormat.Channel)
    return { icon: StreamingIcons.Play, text: "Watch Channel" };
  return { icon: StreamingIcons.Play, text: "Watch Now" };
};

/**
 * Calculates the time difference between when an SLE started and now.
 *
 * @param asset - The AssetResponse object containing event start time details.
 * @returns A formatted string indicating how long ago the event started, or `null` if not applicable.
 *
 * @example
 * ```typescript
 * const asset: AssetResponse = {
 *   attributes: {
 *     displayStartTime: 1764952200000 // Unix timestamp in milliseconds
 *   }
 * };
 *
 * const timeAgo = getEventStartTimeAgo(asset);
 * console.log(timeAgo); // Outputs: "Started 23 minutes ago"
 * ```
 */
const getEventStartTimeAgo = (asset: AssetResponse): string | null => {
  if (!isSportsEvent(asset) || !asset.attributes.displayStartTime) {
    return null;
  }

  // Convert Unix timestamp (milliseconds) to Date
  const startTime = new Date(asset.attributes.displayStartTime);
  const now = new Date();
  const diffInMs = now.getTime() - startTime.getTime();

  // If the event hasn't started yet, return null
  if (diffInMs <= 0) {
    return null;
  }

  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  // Handle different time ranges
  if (diffInMinutes < 1) {
    return "Started less than a minute ago";
  } else if (diffInMinutes === 1) {
    return "Started 1 minute ago";
  } else if (diffInMinutes < 60) {
    return `Started ${diffInMinutes} minutes ago`;
  } else {
    const diffInHours = Math.floor(diffInMinutes / 60);

    if (diffInHours === 1) {
      return "Started 1 hour ago";
    } else return `Started ${diffInHours} hours ago`;
  }
};

export {
  getProviderLogo,
  fetchBadgeString,
  formatTitleBasicsDate,
  fetchGenreString,
  getShowcaseButtonContent,
  getEventStartTimeAgo,
};
